import React, { useEffect, useState, useMemo } from "react";
import { DateTime } from "luxon";
import { toast } from "sonner";
import {
  WEEKDAYS_LONG,
  groupByLocalWeekday,
  todayLocalWeekday,
  formatShowLocalTime,
  isOnAir,
  showStartInWeek,
  localTimezoneLabel,
} from "../lib/schedule-time";
import {
  isPushSupported,
  currentPermission,
  ensurePushSubscription,
  getSubscribedShowIds,
  toggleShowSubscription,
  syncSubscribedShows,
  unsubscribeFromPush,
  ALERT_LEAD_MINUTES,
} from "../lib/web-push";
import { Calendar, Mic2, Send, Lightbulb, Bell, BellOff, BellRing } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SchedulePage = () => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(todayLocalWeekday());
  const [tick, setTick] = useState(0);
  const [subs, setSubs] = useState(() => getSubscribedShowIds());
  const [perm, setPerm] = useState(() => currentPermission());

  // Suggestion form state
  const [suggestion, setSuggestion] = useState({
    show_name: "",
    dj: "",
    description: "",
    contact_email: "",
    preferred_slot: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/shows`);
        if (res.ok && alive) setShows(await res.json());
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const groups = useMemo(() => groupByLocalWeekday(shows), [shows, tick]);
  const now = DateTime.now();

  const handleToggleAlert = async (show) => {
    if (!isPushSupported()) {
      toast.error("Your browser doesn't support push notifications.");
      return;
    }
    const alreadySubscribed = subs.has(show.id);
    // If turning ON, make sure we have push permission + a browser subscription first
    if (!alreadySubscribed) {
      try {
        await ensurePushSubscription();
        setPerm(currentPermission());
      } catch (e) {
        toast.error(e.message || "Couldn't enable notifications");
        setPerm(currentPermission());
        return;
      }
    }
    // Update local list + push server sync
    const next = toggleShowSubscription(show.id);
    setSubs(new Set(next));
    try {
      await syncSubscribedShows();
    } catch {
      /* server will catch up on next visit */
    }
    // If the user just removed their last subscription, drop the push endpoint
    if (alreadySubscribed && next.size === 0) {
      await unsubscribeFromPush();
    }
    if (!alreadySubscribed) {
      toast.success(
        `You'll be alerted ${ALERT_LEAD_MINUTES} min before "${show.name}" goes live.`
      );
    } else {
      toast(`Alert off for "${show.name}"`);
    }
  };

  const submitSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestion.show_name.trim()) {
      toast.error("Show name required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/show-suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(suggestion),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success("Thanks — the booth will review your idea.");
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Something went wrong");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10">
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-1.5 backdrop-blur-sm bg-black/40">
          <Calendar className="w-3.5 h-3.5 text-gold" />
          <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">
            Weekly Schedule
          </span>
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl md:text-7xl leading-[0.9] text-white uppercase">
          Who's on when
        </h1>
        <p className="mt-3 font-body text-neutral-300 text-base md:text-lg max-w-2xl">
          Every show, every DJ, every day of the week — all times shown in your local
          timezone ({localTimezoneLabel()}).
        </p>
      </header>

      {/* Day tabs */}
      <div className="flex gap-1 overflow-x-auto pb-2 mb-4">
        {WEEKDAYS_LONG.map((label, i) => {
          const isToday = i === todayLocalWeekday();
          const isActive = i === activeDay;
          return (
            <button
              key={label}
              data-testid={`schedule-day-${i}`}
              onClick={() => setActiveDay(i)}
              className={`shrink-0 px-4 py-2 rounded-full font-condensed uppercase tracking-widest text-xs transition-colors ${
                isActive
                  ? "bg-gold text-black"
                  : "border border-gold/30 text-neutral-300 hover:border-gold hover:text-gold"
              }`}
            >
              {label}
              {isToday && (
                <span className={`ml-2 text-[9px] ${isActive ? "text-black/60" : "text-crimson-live"}`}>
                  ● TODAY
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Show-alerts banner */}
      <div
        data-testid="alerts-banner"
        className="mb-6 flex items-center gap-3 rounded-lg border border-gold/25 bg-jet-surface px-4 py-3"
      >
        <div className="w-9 h-9 shrink-0 grid place-items-center rounded-full bg-gold/15 text-gold">
          <BellRing className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-condensed uppercase tracking-widest text-[11px] text-gold">
            Never miss your show
          </div>
          <div className="font-body text-xs text-neutral-400 mt-0.5">
            Tap the bell on any show to get a browser alert{" "}
            <span className="text-gold">{ALERT_LEAD_MINUTES} minutes before</span> it goes live.
            {perm === "denied" && (
              <span className="ml-1 text-crimson-live">
                Notifications are blocked — unblock them in your browser settings.
              </span>
            )}
            {subs.size > 0 && (
              <span className="ml-1 text-neutral-300">
                · <b className="text-gold">{subs.size}</b> show{subs.size === 1 ? "" : "s"} set.
              </span>
            )}
          </div>
        </div>
        {perm !== "granted" && perm !== "denied" && perm !== "unsupported" && (
          <button
            data-testid="alerts-enable"
            onClick={async () => {
              try {
                await ensurePushSubscription();
                setPerm(currentPermission());
                toast.success("Alerts enabled");
              } catch (e) {
                toast.error(e.message || "Couldn't enable alerts");
                setPerm(currentPermission());
              }
            }}
            className="shrink-0 inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[10px] px-3 py-2 rounded-full transition-colors"
          >
            <Bell className="w-3.5 h-3.5" />
            Enable
          </button>
        )}
        {perm === "unsupported" && (
          <span className="shrink-0 inline-flex items-center gap-1.5 text-neutral-500 text-[10px] font-condensed uppercase tracking-widest">
            <BellOff className="w-3.5 h-3.5" />
            Not supported
          </span>
        )}
      </div>

      {/* Show list for active day */}
      <section
        data-testid={`schedule-list-${activeDay}`}
        className="bg-jet-surface border border-gold/20 rounded-lg overflow-hidden mb-10"
      >
        {loading ? (
          <div className="p-8 text-center text-neutral-500 text-sm">Loading schedule…</div>
        ) : groups[activeDay].length === 0 ? (
          <div className="p-10 text-center text-neutral-500">
            No shows scheduled for {WEEKDAYS_LONG[activeDay]} yet.
          </div>
        ) : (
          <ul className="divide-y divide-neutral-900">
            {groups[activeDay].map((s) => {
              const live = isOnAir(s, now);
              const startLocal = showStartInWeek(s).setZone(DateTime.local().zoneName);
              const hour = startLocal.toFormat("h:mm a");
              return (
                <li
                  key={s.id}
                  data-testid={`schedule-show-${s.id}`}
                  className={`flex items-start gap-4 px-5 py-4 ${
                    live ? "bg-crimson/10" : ""
                  }`}
                >
                  <div className="w-24 shrink-0 pt-1">
                    <div
                      className={`font-condensed uppercase tracking-widest text-xs ${
                        live ? "text-crimson-live" : "text-gold"
                      }`}
                    >
                      {hour}
                    </div>
                    <div className="font-body text-[11px] text-neutral-500 mt-0.5">
                      {formatShowLocalTime(s)}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-xl text-white uppercase leading-tight">
                        {s.name}
                      </div>
                      {live && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-crimson/25 text-crimson-live font-condensed uppercase tracking-widest text-[10px]">
                          <span className="w-1.5 h-1.5 rounded-full bg-crimson-live live-dot" />
                          On Air
                        </span>
                      )}
                    </div>
                    {s.dj && (
                      <div className="font-body text-sm text-gold/90 mt-0.5">With {s.dj}</div>
                    )}
                    {s.description && (
                      <div className="font-body text-sm text-neutral-400 mt-1">{s.description}</div>
                    )}
                  </div>
                  <button
                    data-testid={`schedule-alert-toggle-${s.id}`}
                    onClick={() => handleToggleAlert(s)}
                    aria-label={subs.has(s.id) ? "Turn alert off" : "Alert me before this show"}
                    title={
                      subs.has(s.id)
                        ? `Alert set — you'll be pinged ${ALERT_LEAD_MINUTES} min before`
                        : "Alert me before this show"
                    }
                    className={`shrink-0 self-start mt-1 w-9 h-9 grid place-items-center rounded-full border transition-colors ${
                      subs.has(s.id)
                        ? "border-gold bg-gold/15 text-gold hover:bg-gold/25"
                        : "border-neutral-800 text-neutral-500 hover:border-gold hover:text-gold"
                    }`}
                  >
                    {subs.has(s.id) ? (
                      <BellRing className="w-4 h-4" />
                    ) : (
                      <Bell className="w-4 h-4" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Suggest a show */}
      <section
        data-testid="suggest-show-section"
        className="bg-jet-surface border border-gold/25 rounded-lg overflow-hidden"
      >
        <div className="flex items-center gap-2 px-5 py-4 border-b border-neutral-900">
          <Lightbulb className="w-4 h-4 text-gold" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Suggest A Show
          </h2>
        </div>

        {submitted ? (
          <div data-testid="suggest-thankyou" className="p-8 text-center">
            <div className="mx-auto w-12 h-12 grid place-items-center rounded-full bg-gold/20 text-gold mb-2">
              <Mic2 className="w-5 h-5" />
            </div>
            <div className="font-display text-2xl text-white uppercase">Suggestion sent</div>
            <p className="mt-2 font-body text-sm text-neutral-400 max-w-md mx-auto">
              Thanks — the booth reviews every idea. If we like it we'll reach out at
              {suggestion.contact_email ? ` ${suggestion.contact_email}` : " the contact info you leave here"}.
            </p>
            <button
              data-testid="suggest-another"
              onClick={() => {
                setSubmitted(false);
                setSuggestion({
                  show_name: "",
                  dj: "",
                  description: "",
                  contact_email: "",
                  preferred_slot: "",
                });
              }}
              className="mt-4 inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-xs px-4 py-2 rounded-full transition-colors"
            >
              Suggest Another
            </button>
          </div>
        ) : (
          <form onSubmit={submitSuggestion} className="p-5 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                Show name
              </label>
              <input
                data-testid="suggest-name"
                value={suggestion.show_name}
                onChange={(e) => setSuggestion({ ...suggestion, show_name: e.target.value })}
                maxLength={120}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                Host / DJ (optional)
              </label>
              <input
                data-testid="suggest-dj"
                value={suggestion.dj}
                onChange={(e) => setSuggestion({ ...suggestion, dj: e.target.value })}
                maxLength={80}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                What's the vibe?
              </label>
              <textarea
                data-testid="suggest-description"
                value={suggestion.description}
                onChange={(e) => setSuggestion({ ...suggestion, description: e.target.value })}
                rows={4}
                maxLength={1000}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none resize-y"
              />
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                Preferred slot (optional)
              </label>
              <input
                data-testid="suggest-slot"
                value={suggestion.preferred_slot}
                onChange={(e) => setSuggestion({ ...suggestion, preferred_slot: e.target.value })}
                placeholder="e.g. Sunday evenings"
                maxLength={80}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>
            <div>
              <label className="block font-condensed uppercase tracking-widest text-[10px] text-neutral-400 mb-1">
                Your email (optional)
              </label>
              <input
                data-testid="suggest-email"
                type="email"
                value={suggestion.contact_email}
                onChange={(e) => setSuggestion({ ...suggestion, contact_email: e.target.value })}
                maxLength={200}
                className="w-full bg-neutral-900 border border-neutral-800 focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <button
                data-testid="suggest-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light disabled:opacity-50 text-black font-condensed uppercase tracking-widest text-xs px-6 py-3 rounded-full transition-colors"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Sending…" : "Send Suggestion"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default SchedulePage;
